<template>
  <div :class="$style.search" ref="searchContainer">
    <vgp-input
      ref="inputRef"
      :model-value="modelValue"
      :class="[$style.search__input, { [$style['search__input--loading']]: isLoading }]"
      :small="true"
      :border="true"
      :icons-left="iconsLeft"
      :placeholder="locales('search_our_help_center')"
      @update:modelValue="handleInputChange"
      @keydown.enter="handleEnter"
      @keydown.down="handleArrowDown"
      @keydown.up="handleArrowUp"
      @keydown.esc="handleEscape"
      :icons-right="iconsRight"
    />
    <learning-center-search-dropdown
      ref="dropdownRef"
      :search-query="modelValue"
      :is-visible="showDropdown && !isNoResults && !isLoading"
      :selected-index="selectedIndex"
      @change-selected-index="selectedIndex = $event"
      @tutorial-click="handleTutorialClick"
      @help-center-click="handleHelpCenterClick"
      @close="handleDropdownClose"
      @loading="isLoading = $event"
    />
    <search-loader
     v-if="isLoading"
     :size="17"
     :class="$style.search__loader"
     />
  </div>
</template>

<script>
import SearchLoader from '$$vueCmp/loaders/searchLoader.vue';
import vgpInput from '../../../../common/VueComponents/vInput/vgpInput.vue';
import LearningCenterSearchDropdown from './learningCenterSearchDropdown.vue';

const ZENDESK_LOCALE_MAP = {
  'en': 'en-us',
  'en-us': 'en-us',
  'ru': 'ru',
  'ru-ru': 'ru',
  'es': 'es',
  'es-es': 'es',
  'es-mx': 'es',
};

function getHelpCenterLocale(raw) {
  if (!raw) {
    return 'en-us';
  }
  const locale = raw.toLowerCase();
  if (ZENDESK_LOCALE_MAP[locale]) {
    return ZENDESK_LOCALE_MAP[locale];
  }
  const base = locale.split('-')[0];
  if (ZENDESK_LOCALE_MAP[base]) {
    return ZENDESK_LOCALE_MAP[base];
  }
  return 'en-us';
}

const LOCALE = getHelpCenterLocale(window.user?.locale);
const HELP_CENTER_URL = `https://help.ganttpro.com/hc/${LOCALE}`;

export default {
  name: 'LearningCenterSearch',
  components: {
    vgpInput,
    SearchLoader,
    LearningCenterSearchDropdown,
  },
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    isNoResults: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'tutorial-click', 'help-center-search'],
  data() {
    return {
      locales: __,
      showDropdown: false,
      selectedIndex: -1,
      isLoading: false,
    };
  },
  watch: {
    modelValue(newValue) {
      this.showDropdown = !!newValue && newValue.trim().length > 0;
      if (this.showDropdown) {
        this.selectedIndex = -1;
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  computed: {
    iconsLeft() {
      return [{
        name: 'search',
        type: 'regular',
        size: 24,
        color: this.isLoading ? 'transparent' : '#B2B2B2'
      }];
    },
    iconsRight() {
      if (!this.modelValue || this.modelValue.trim() === '') {
        return [];
      }
      return [{
        name: 'close-5',
        type: 'bold',
        size: 24,
        handler: this.handleClear,
      }];
    },
    hasSearchResults() {
      if (!this.$refs.dropdownRef || !this.showDropdown) {
        return false;
      }
      const helpCenterResults = this.$refs.dropdownRef.helpCenterResults || [];
      return helpCenterResults.length > 0;
    },
    totalDropdownItems() {
      if (!this.showDropdown || !this.$refs.dropdownRef) {
        return 0;
      }
      const tutorialCount = this.$refs.dropdownRef.tutorialResults?.length || 0;
      const helpCenterCount = this.$refs.dropdownRef.helpCenterResults?.length || 0;
      return tutorialCount + helpCenterCount;
    },
  },
  methods: {
    handleInputChange(value) {
      this.$emit('update:modelValue', value);
    },
    handleClear() {
      this.$emit('update:modelValue', '');
      this.showDropdown = false;
    },
    handleEnter() {
      if (!this.modelValue || !this.modelValue.trim()) {
        return;
      }

      // if dropdown is open and there is a selected item, select it
      if (this.showDropdown && this.selectedIndex >= 0 && this.$refs.dropdownRef) {
        this.$refs.dropdownRef.selectCurrentItem();
        return;
      }

      const query = this.modelValue.trim();
      const hasResults = this.hasSearchResults;

      this.showDropdown = false;
      this.selectedIndex = -1;

      this.$emit('help-center-search', {
        query,
        hasResults,
      });

      if (hasResults) {
      const searchUrl = `${HELP_CENTER_URL}/search?query=${encodeURIComponent(query)}`;
      window.open(searchUrl, '_blank');
      }
    },
    handleTutorialClick(result) {
      this.showDropdown = false;
      this.selectedIndex = -1;
      this.$emit('tutorial-click', result);
    },
    handleHelpCenterClick(result) {
      this.showDropdown = false;
      this.selectedIndex = -1;
      window.open(result.url, '_blank');
    },
    handleClickOutside(event) {
      if (this.$refs.searchContainer && !this.$refs.searchContainer.contains(event.target)) {
        this.showDropdown = false;
        this.selectedIndex = -1;
      }
    },
    handleDropdownClose() {
      this.showDropdown = false;
      this.selectedIndex = -1;
    },
    handleArrowDown(event) {
      if (!this.showDropdown || this.totalDropdownItems === 0) {
        return;
      }
      event.preventDefault();
      this.selectedIndex = (this.selectedIndex + 1) % this.totalDropdownItems;
    },
    handleArrowUp(event) {
      if (!this.showDropdown || this.totalDropdownItems === 0) {
        return;
      }
      event.preventDefault();
      this.selectedIndex = this.selectedIndex <= 0 ? this.totalDropdownItems - 1 : this.selectedIndex - 1;
    },
    handleEscape() {
      if (this.showDropdown) {
        this.showDropdown = false;
        this.selectedIndex = -1;
      }
    },
  },
};
</script>

<style module src="./search.less" lang="less"></style>

